/*
 * Names that would be drawn over another creature, and how they are put
 * behind it. A name floats a little above its own creature's head, which
 * is where the body of whoever stands a tile north is drawn, and a name
 * laid over somebody else reads as theirs and hides them.
 *
 * Such a name was left off altogether from 2026-10-01 ("Don't show the
 * names of creatures in the map over another creature"). Since 2026-10-07
 * it is drawn behind the creature it would cover instead ("there's
 * something that prevents rendering a creature's name if another creature
 * would be obscured by it. instead, render the creature's name *behind*
 * the obscured creature instead", and of a player standing a tile
 * south-east of Grubb: "this player's name should appear but grubb's
 * sprite should be rendered over the top of the player's name"). So the
 * overlap test below no longer decides which names are hidden but which
 * creatures go over which names, and nameBehind lays such a name with
 * those creatures' figures cut out of it: the rest of the name shows, and
 * where a figure is, the figure does, exactly as the scene painted it.
 *
 * Kept apart from the renderer so the tests can run it without a canvas.
 */

/** A box on the screen: left, top, width, height. */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** One creature as the nameplates see it: its name's box and its body's. */
export interface Plate {
  id: number;
  name: Box;
  body: Box;
}

function crosses(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * For each creature whose name would cross other creatures' bodies, the
 * ids of those creatures: they go over its name. A name that crosses
 * nobody is not in the map.
 */
export function creaturesOverNames(plates: Plate[]): Map<number, number[]> {
  const over = new Map<number, number[]>();
  for (const p of plates) {
    for (const o of plates) {
      if (o.id === p.id || !crosses(p.name, o.body)) continue;
      const ids = over.get(p.id);
      if (ids) ids.push(o.id);
      else over.set(p.id, [o.id]);
    }
  }
  return over;
}

/** A figure as the scene painted it: its sprite and the sprite's top left. */
export interface Figure {
  spr: CanvasImageSource;
  L: number;
  T: number;
}

/** A name to lay behind figures: see namesBehind. */
export interface BehindName {
  box: Box;
  label: (into: CanvasRenderingContext2D) => void;
  over: readonly Figure[];
  /** The alpha it is laid on the scene at. */
  alpha: number;
}

/**
 * Lay a name inside `box` (world pixels) behind the figures in `over`.
 *
 * The name is drawn by `label` onto a layer of its own, the size of the
 * box on the glass and under the same transform as the scene, and every
 * figure that goes over it is drawn onto that layer with destination-out,
 * which cuts the figure's shape out of the name. The layer is then laid
 * over the scene at the context's alpha. The scene under the name is never
 * painted again: where a figure covers the name, what shows is the figure
 * as it was drawn — its frame, its tint, the dark on it, or whatever stands
 * in front of it — and nothing has to be redrawn to look the same.
 *
 * `layer` hands back a context at least w by h device pixels, reused from
 * name to name and frame to frame. The transform is a scale and a shift,
 * which is all the scene uses.
 */
export function nameBehind(
  ctx: CanvasRenderingContext2D,
  layer: (w: number, h: number) => CanvasRenderingContext2D,
  box: Box,
  label: (into: CanvasRenderingContext2D) => void,
  over: readonly Figure[],
) {
  namesBehind(ctx, layer, [{ box, label, over, alpha: ctx.globalAlpha }]);
}

/**
 * nameBehind for several names laid one after another, each at its own
 * alpha, as it would lay them one at a time — but each on its own strip
 * of the one layer, every one of them written before any is laid on the
 * scene. A layer written again after it has been laid has to be copied
 * first, since the scene still has to be drawn from what it was, and on
 * a phone's GPU that is a stall a name: a crowd of fifty with their names
 * behind each other spent more on it than on the rest of the frame
 * (2026-10-23: "The game is getting laggy on phone, optimise it").
 *
 * Each strip is clipped to itself, so a name or a figure reaching past
 * its box marks nothing of the next one's, as the edge of a layer the
 * size of the box held it before.
 */
export function namesBehind(
  ctx: CanvasRenderingContext2D,
  layer: (w: number, h: number) => CanvasRenderingContext2D,
  names: readonly BehindName[],
) {
  const m = ctx.getTransform();
  const strips: { n: BehindName; dx0: number; dy0: number; dw: number; dh: number; top: number }[] = [];
  let W = 0,
    H = 0;
  for (const n of names) {
    const { box } = n;
    const dx0 = Math.floor(m.a * box.x + m.e), dy0 = Math.floor(m.d * box.y + m.f);
    const dw = Math.ceil(m.a * (box.x + box.w) + m.e) - dx0, dh = Math.ceil(m.d * (box.y + box.h) + m.f) - dy0;
    if (dw <= 0 || dh <= 0) continue;
    strips.push({ n, dx0, dy0, dw, dh, top: H });
    W = Math.max(W, dw);
    H += dh;
  }
  if (!strips.length) return;
  const lc = layer(W, H);
  lc.setTransform(1, 0, 0, 1, 0, 0);
  lc.globalAlpha = 1;
  lc.globalCompositeOperation = "source-over";
  lc.clearRect(0, 0, W, H);
  lc.imageSmoothingEnabled = ctx.imageSmoothingEnabled;
  for (const { n, dx0, dy0, dw, dh, top } of strips) {
    lc.save();
    lc.beginPath();
    lc.rect(0, top, dw, dh);
    lc.clip();
    lc.setTransform(m.a, m.b, m.c, m.d, m.e - dx0, m.f - dy0 + top);
    n.label(lc);
    lc.globalCompositeOperation = "destination-out";
    for (const f of n.over) lc.drawImage(f.spr, f.L, f.T);
    lc.restore();
  }
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  for (const { n, dx0, dy0, dw, dh, top } of strips) {
    ctx.globalAlpha = n.alpha;
    ctx.drawImage(lc.canvas, 0, top, dw, dh, dx0, dy0, dw, dh);
  }
  ctx.restore();
}
